import React from 'react';

import Image from '$partials/media/image';
import Button from '$partials/ui/button.jsx';

import { IconArrowRight, IconChevronSprite } from '$partials/icons/index.js';
import { useLocale } from '$contexts/locale.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').BvaImageCardGridItem}
 */
const ImageCard = ({
    heading,
    bodyCopy,
    ctaLabel,
    ctaLink,
    image,
    eyebrowIcon
}) => {
    const { formatURI } = useLocale();

    return (
        <div className='overflow-hidden flex flex-col h-full justify-between expand-interaction group'>
            <div className='overflow-hidden rounded pointer-events-none select-none relative'>
                {image && (
                    <div className='aspect-square w-full h-full'>
                        <Image
                            className='h-full object-cover'
                            containerClassName='h-full object-cover'
                            height={'100%'}
                            loading={image?.loading}
                            loadWidth={840}
                            src={image?.src}
                            width={'100%'} />
                    </div>
                )}
                <div className='absolute inset-0 bg-[#0000000D] opacity-0 group-hover:opacity-100 transition-opacity rounded' />
                {eyebrowIcon && (
                    <div className='absolute top-16 left-16 bg-grey-0 rounded p-8 w-68'>
                        <Image
                            height={eyebrowIcon.height}
                            loadWidth={100}
                            src={eyebrowIcon?.src}
                            width={eyebrowIcon.width} />
                    </div>
                )}
            </div>
            <h4 className='type-style-4 text-text-primary mt-16'>
                {heading}
            </h4>

            {bodyCopy && (
                <p className='type-style-body text-text-secondary mt-5'>
                    {bodyCopy}
                </p>
            )}
            {ctaLabel && ctaLink && (
                <div className='mt-14 flex self-start'>
                    <Button
                        as='a'
                        className='link link--large-brand expand-interaction__action'
                        href={formatURI(ctaLink)}>
                        <span className='button__label--animate'>
                            <span>
                                {ctaLabel}
                            </span>
                        </span>
                        <span className='button__icon button__icon--animate'>
                            <IconChevronSprite />
                        </span>
                    </Button>
                </div>
            )}
        </div>
    );

};

/**
 * @type {import('$types/components/partials/modules.d.ts').ModuleImageCardGrid}
 */

const ModuleImageCardGrid = ({
    bodyCopy,
    eyebrow,
    heading,
    itemsCollection,
    cta1Label,
    cta1Link,
    cta2Label,
    cta2Link,
    headerAlignment,
    isArticlePage,
}) => {
    const items = itemsCollection?.items ?? [];
    const { formatURI } = useLocale();

    return (
        <section className={`relative py-40${isArticlePage ? '' : ' lg:py-80'}`}>
            <div className='layout-container layout-site-outer-padding'>
                <div className={`col-padding sm:w-10/12 md:w-7/12 lg:w-6/12 col-wrapper flex flex-col ${headerAlignment === 'Left' ? '' : 'text-center justify-self-center items-center'}`}>
                    {eyebrow && (
                        <p
                            className='type-style-eyebrow text-text-accent mb-12'>
                            {eyebrow}
                        </p>
                    )}
                    <h2 className={`${isArticlePage ? 'type-style-4' : 'type-style-3-extended'} text-text-primary mb-10`}>
                        {heading}
                    </h2>
                    {bodyCopy && (
                        <p className='type-style-body text-text-secondary'>
                            {bodyCopy}
                        </p>
                    )}
                    {/* cta buttons */}
                    {(cta1Link || cta2Link) && (
                        <div className='flex flex-wrap gap-8 mt-16'>
                            {cta1Link && (
                                <Button
                                    as='a'
                                    className='button button--primary'
                                    href={formatURI(cta1Link)}>
                                    <span className='button__label--animate'>
                                        <span>{cta1Label}</span>
                                    </span>
                                </Button>
                            )}
                            {cta2Link && (
                                <Button
                                    as='a'
                                    className='button button--secondary'
                                    href={formatURI(cta2Link)}>
                                    <span className='button__label--animate'>
                                        <span>
                                            {cta2Label}
                                        </span>
                                    </span>
                                </Button>
                            )}
                        </div>
                    )}
                </div>
                {items.length > 0 &&
                    <div className='flex flex-wrap mt-40 gap-y-40 md:gap-y-80 col-wrapper'>
                        {/** @type {any[]} */ (items).map((itm, i) => {
                            return (
                                <div
                                    className={`col-padding w-full sm:w-6/12 ${items?.length === 2 ? 'md:w-6/12' : 'md:w-4/12'}`}
                                    key={i}>
                                    <ImageCard
                                        {...itm} />
                                </div>
                            );
                        })}
                    </div>}
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

export default ModuleImageCardGrid;
